
import {defineComponent, PropType} from "vue";
import {BackgroundImageOverlayRestDto} from "@/api/pharma-cms-content-page/models";
import {BackgroundImageOverlayRestDtoModel} from "@/models/api/pharma-cms-content-page/BackgroundImageOverlayRestDtoModel";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import BackgroundImageOverlayDetail from "@/components/layouts/content-pages/wysiwyg/misc/BackgroundImageOverlayDetail.vue";
import MetadataGroupCard from "@/components/layouts/content-pages/wysiwyg/misc/MetadataGroupCard.vue";
import BaseDeleteButton from "@/components/UI/Bootstrap/BaseDeleteButton.vue";

export default defineComponent({
  name: "BackgroundImageOverlaysDetail",
  mixins: [ComponentMixin],
  components: {GenericComponentWrapper, BackgroundImageOverlayDetail, MetadataGroupCard, BaseDeleteButton},
  emits: ['update:modelValue'],
  props: {
    // background_image.overlays — null, undefined or empty all mean the group is off.
    modelValue: {
      type: Array as PropType<BackgroundImageOverlayRestDto[] | null>,
      required: false,
      default: null,
    },
    name: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.persistedDto = this.cloneOverlays(this.modelValue);

    this.componentUI.setReady();
  },
  data() {
    return {
      enabled: false as boolean,
      localDto: [] as BackgroundImageOverlayRestDto[],
      // Overlays entered before the group was switched off, so they can be restored.
      lastActiveDto: null as BackgroundImageOverlayRestDto[] | null,
      // Reset baseline: the overlays as they are stored in the database.
      persistedDto: null as BackgroundImageOverlayRestDto[] | null,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: BackgroundImageOverlayRestDto[] | null | undefined) {
        this.enabled = (newValue?.length ?? 0) > 0;
        if (newValue?.length) {
          this.localDto = this.cloneOverlays(newValue) as BackgroundImageOverlayRestDto[];
        }
      },
    },
  },
  methods: {
    cloneOverlays(source: BackgroundImageOverlayRestDto[] | null | undefined): BackgroundImageOverlayRestDto[] | null {
      if (!source?.length) {
        return null;
      }

      return source.map((overlay: BackgroundImageOverlayRestDto) => BackgroundImageOverlayRestDtoModel.cloneFrom(overlay));
    },
    onEnabledChanged(newValue: boolean): void {
      if (newValue === this.enabled) {
        return;
      }
      this.enabled = newValue;

      if (newValue) {
        this.localDto = this.cloneOverlays(this.lastActiveDto) ?? [BackgroundImageOverlayRestDtoModel.createWithDefaults()];

        this.emitModelChanged();
      } else {
        this.lastActiveDto = this.cloneOverlays(this.localDto);

        this.$emit('update:modelValue', null);
      }
    },
    resetToPersisted(): void {
      this.localDto = this.cloneOverlays(this.persistedDto) ?? [BackgroundImageOverlayRestDtoModel.createWithDefaults()];

      this.emitModelChanged();
    },
    addOverlay(): void {
      this.localDto.push(BackgroundImageOverlayRestDtoModel.createWithDefaults());
      this.emitModelChanged();
    },
    removeOverlay(index: number): void {
      this.localDto.splice(index, 1);

      if (!this.localDto.length) {
        // An empty list means no overlays at all, so the group switches itself off.
        this.enabled = false;
        this.$emit('update:modelValue', null);
        return;
      }

      this.emitModelChanged();
    },
    emitModelChanged(): void {
      this.$emit('update:modelValue', this.localDto);
    },
  },
});
